import {
  NetworkInspectionAccumulator,
  type NetworkInspectionSnapshot,
  decodeNetworkInspectionEvent,
} from "@/lib/networkInspection";
import { useEffect, useState } from "react";
import useSWR from "swr";

export type ActiveTunnel = {
  tunnelId: string;
  state: "starting" | "ready" | "stopping";
  selectors: Array<{
    id: string;
    kind: "route" | "domain";
    value: string;
  }>;
  inspection: {
    enabled: boolean;
    captureBodies: boolean;
    maxBodyBytes: number;
  };
};

export type TunnelStatus = {
  active?: ActiveTunnel;
};

export type LiveNetworkInspection = NetworkInspectionSnapshot & {
  connection: "idle" | "connecting" | "live" | "reconnecting";
};

type StoredLiveNetworkInspection = LiveNetworkInspection & {
  streamKey?: string;
};

const EMPTY_LIVE_INSPECTION: StoredLiveNetworkInspection = {
  requests: [],
  warnings: [],
  connection: "idle",
};

export function useTunnelStatus(
  apiUrl: string | undefined,
  token: string | undefined,
  enabled: boolean,
): { status: TunnelStatus | "loading"; error?: Error } {
  const { data, error } = useSWR<TunnelStatus>(
    enabled && apiUrl && token ? ["tunnelStatus", apiUrl, token] : null,
    () => fetchTunnelStatus(apiUrl as string, token as string),
    { refreshInterval: 3_000 },
  );
  return {
    status: data ?? "loading",
    error: error instanceof Error ? error : undefined,
  };
}

export function useLiveNetworkInspection(
  apiUrl: string | undefined,
  token: string | undefined,
  tunnelId: string | undefined,
  enabled: boolean,
): LiveNetworkInspection {
  const streamKey =
    enabled && apiUrl && token && tunnelId
      ? `${apiUrl}\u0000${tunnelId}\u0000${token}`
      : undefined;
  const [state, setState] = useState<StoredLiveNetworkInspection>(
    EMPTY_LIVE_INSPECTION,
  );

  useEffect(() => {
    if (!streamKey || !apiUrl || !token || !tunnelId) return;

    const accumulator = new NetworkInspectionAccumulator();
    const source = new EventSource(
      buildRuntimeTokenUrl(
        apiUrl,
        `/tunnel/${encodeURIComponent(tunnelId)}/inspection/events`,
        token,
      ),
    );
    let connection: LiveNetworkInspection["connection"] = "connecting";
    let closed = false;

    const publish = () => {
      if (closed) return;
      setState({ ...accumulator.snapshot(), connection, streamKey });
    };
    source.onopen = () => {
      connection = "live";
      publish();
    };
    source.onerror = () => {
      // EventSource owns retry timing and Last-Event-ID. Keeping this
      // object alive is what gives reconnects exact resume semantics.
      connection = "reconnecting";
      publish();
    };
    source.onmessage = (message) => {
      try {
        if (
          accumulator.consume(
            decodeNetworkInspectionEvent(message.lastEventId, message.data),
          )
        ) {
          publish();
        }
      } catch (error) {
        accumulator.addWarning(
          `Ignored malformed inspection event: ${
            error instanceof Error ? error.message : String(error)
          }`,
        );
        publish();
      }
    };

    return () => {
      closed = true;
      source.close();
    };
  }, [apiUrl, streamKey, token, tunnelId]);

  if (!streamKey) return EMPTY_LIVE_INSPECTION;
  return state.streamKey === streamKey
    ? state
    : { ...EMPTY_LIVE_INSPECTION, connection: "connecting" };
}

export async function fetchTunnelStatus(
  apiUrl: string,
  token: string,
): Promise<TunnelStatus> {
  const response = await fetch(
    buildRuntimeTokenUrl(apiUrl, "/tunnel/status", token),
  );
  if (!response.ok) {
    throw new Error(`failed to load tunnel status: HTTP ${response.status}`);
  }
  return decodeTunnelStatus(await response.json());
}

export function buildRuntimeTokenUrl(
  apiUrl: string,
  path: string,
  token: string,
): string {
  const url = new URL(apiUrl);
  url.pathname = `${withoutTrailingSlashes(url.pathname)}${path}`;
  url.search = "";
  url.searchParams.set("token", token);
  url.hash = "";
  return url.toString();
}

function withoutTrailingSlashes(path: string): string {
  let end = path.length;
  while (end > 0 && path.charCodeAt(end - 1) === 47) end--;
  return path.slice(0, end);
}

export function decodeTunnelStatus(value: unknown): TunnelStatus {
  const status = asRecord(value, "tunnel status");
  if (status.active === undefined || status.active === null) return {};
  const active = asRecord(status.active, "active tunnel");
  const state = asString(active.state, "active tunnel state");
  if (state !== "starting" && state !== "ready" && state !== "stopping") {
    throw new Error(`invalid active tunnel state ${state}`);
  }
  if (!Array.isArray(active.selectors)) {
    throw new Error("active tunnel selectors must be an array");
  }
  const inspection = asRecord(active.inspection, "active tunnel inspection");
  if (
    typeof inspection.enabled !== "boolean" ||
    typeof inspection.captureBodies !== "boolean" ||
    typeof inspection.maxBodyBytes !== "number"
  ) {
    throw new Error("active tunnel inspection is invalid");
  }
  return {
    active: {
      tunnelId: asNonEmptyString(active.tunnelId, "active tunnelId"),
      state,
      selectors: active.selectors.map((item) => {
        const selector = asRecord(item, "active tunnel selector");
        const kind = asString(selector.kind, "selector kind");
        if (kind !== "route" && kind !== "domain") {
          throw new Error(`invalid selector kind ${kind}`);
        }
        return {
          id: asNonEmptyString(selector.id, "selector id"),
          kind,
          value: asNonEmptyString(selector.value, "selector value"),
        };
      }),
      inspection: {
        enabled: inspection.enabled,
        captureBodies: inspection.captureBodies,
        maxBodyBytes: inspection.maxBodyBytes,
      },
    },
  };
}

function asRecord(value: unknown, name: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${name} must be an object`);
  }
  return value as Record<string, unknown>;
}

function asString(value: unknown, name: string): string {
  if (typeof value !== "string") throw new Error(`${name} must be a string`);
  return value;
}

function asNonEmptyString(value: unknown, name: string): string {
  const result = asString(value, name);
  if (!result) throw new Error(`${name} must not be empty`);
  return result;
}
